/**
 * FEAnimator.ts
 * -----------------------------------------------------------------------------
 * Computes every Element Results "Play" animation frame entirely client-side.
 *
 * .NET sends TWO payloads for a whole Play session, both O(joint count) —
 * never O(frame count × triangle count) like earlier attempts:
 *   1. loadFEAnimationMesh — ONCE: undeformed base joint positions (dense,
 *      indexed by AnalysisIndex) plus, for every representation (joints/
 *      frames/links/shellTris/shellEdges/restraints), a parallel joint-id
 *      array so a vertex's position is just "look up its joint". Frame/shell
 *      extrusion can't reduce to a lookup — its geometry depends on the
 *      deformed endpoints each frame — so it ships polygon/thickness metadata
 *      instead and this class rebuilds the prism/slab itself.
 *   2. playFEScaleAnimation / playFEStepAnimation — ONCE per Play click: one
 *      (or one-per-step) compact per-joint displacement+color field.
 *
 * From there, every frame is computed here and written straight into the live
 * FEScene buffers (see FEScene.applyAnimationFrame/applyFrameExtrude/
 * applyShellExtrude) — no further interop until Stop.
 *
 * The extrusion math (extrudeFrame/extrudeShell) is a line-for-line port of
 * FEMeshExporter.cs's ExtrudeFrame/ExtrudeShell: same cross-section polygon,
 * same vector math, in the same raw mm/Z-up model space. The mm→m, Z-up→Y-up
 * remap (matching AddVertexXYZ) is applied only when a vertex is written into
 * a buffer, exactly like the C# source applies it only at emission time.
 */

import type { FEScene } from './FEMesh';

const MM_TO_M = 0.001;

interface FrameExtrudeMeta { p1: number; p2: number; poly: [number, number][]; }
interface ShellExtrudeMeta { nodes: number[]; thickness: number; }

export interface AnimMeshPayload {
  n: number;
  basePos: number[];        // dense, 3 per AnalysisIndex (mm, model axes)
  jointIds: number[];
  restraintIds: number[];
  frameIds: number[];       // 2 per frame
  linkIds: number[];        // 2 per link
  shellTriIds: number[];    // 3 per shell-triangle-fan vertex
  shellEdgeIds: number[];   // 2 per edge segment
  frameExtrude: FrameExtrudeMeta[];
  shellExtrude: ShellExtrudeMeta[];
}

export interface DisplacementField {
  n: number;
  dx: number[]; dy: number[]; dz: number[];
  r?: number[]; g?: number[]; b?: number[];
}

type Vec3 = [number, number, number];

function writeVertexMm(arr: Float32Array, offset: number, p: Vec3): void {
  arr[offset] = p[0] * MM_TO_M;
  arr[offset + 1] = p[2] * MM_TO_M;
  arr[offset + 2] = -p[1] * MM_TO_M;
}

export class FEAnimator {
  private mesh: AnimMeshPayload | null = null;
  private baseX = new Float64Array(0);
  private baseY = new Float64Array(0);
  private baseZ = new Float64Array(0);

  // Scratch buffers reused across frames — sized once in loadMesh, never
  // reallocated per tick.
  private px = new Float64Array(0);
  private py = new Float64Array(0);
  private pz = new Float64Array(0);
  private frameTriBuf: Float32Array | null = null;
  private frameTriColorBuf: Float32Array | null = null;
  private shellExtrudeTriBuf: Float32Array | null = null;
  private shellExtrudeTriColorBuf: Float32Array | null = null;

  constructor(private readonly fe: FEScene) {}

  public get ready(): boolean {
    return this.mesh != null;
  }

  public loadMesh(json: string): boolean {
    let m: AnimMeshPayload;
    try {
      m = typeof json === 'string' ? JSON.parse(json) : json;
    } catch (e) {
      console.error('[BridgeViewport] failed to parse FE animation mesh', e);
      this.mesh = null;
      return false;
    }
    this.mesh = m;
    const n = m.n;
    this.baseX = new Float64Array(n);
    this.baseY = new Float64Array(n);
    this.baseZ = new Float64Array(n);
    for (let i = 0; i < n; i++) {
      this.baseX[i] = m.basePos[i * 3] ?? 0;
      this.baseY[i] = m.basePos[i * 3 + 1] ?? 0;
      this.baseZ[i] = m.basePos[i * 3 + 2] ?? 0;
    }
    this.px = new Float64Array(n);
    this.py = new Float64Array(n);
    this.pz = new Float64Array(n);

    // Extruded-triangle buffers are fixed-size: a frame's triangle count is
    // fully determined by its cross-section's side count (box=4, cylinder=12),
    // a shell's by its node count — neither changes frame to frame.
    let frameFloats = 0;
    for (const fx of m.frameExtrude) { const sides = fx.poly.length; frameFloats += Math.max(0, 4 * sides - 4) * 9; }
    this.frameTriBuf = frameFloats > 0 ? new Float32Array(frameFloats) : null;
    this.frameTriColorBuf = frameFloats > 0 ? new Float32Array(frameFloats) : null;

    let shellFloats = 0;
    for (const sx of m.shellExtrude) { const cnt = sx.nodes.length; shellFloats += Math.max(0, 4 * cnt - 4) * 9; }
    this.shellExtrudeTriBuf = shellFloats > 0 ? new Float32Array(shellFloats) : null;
    this.shellExtrudeTriColorBuf = shellFloats > 0 ? new Float32Array(shellFloats) : null;

    return true;
  }

  /** Compute one frame — a raw displacement field scaled by `scale` — and push
   *  it into the live geometry buffers. No-op until loadMesh() has succeeded. */
  public applyFrame(field: DisplacementField, scale: number): void {
    const m = this.mesh;
    if (!m) return;
    const n = m.n;
    const hasColor = !!(field.r && field.g && field.b);

    for (let i = 0; i < n; i++) {
      this.px[i] = this.baseX[i] + (field.dx[i] ?? 0) * scale;
      this.py[i] = this.baseY[i] + (field.dy[i] ?? 0) * scale;
      this.pz[i] = this.baseZ[i] + (field.dz[i] ?? 0) * scale;
    }

    this.fe.applyAnimationFrame(m, this.px, this.py, this.pz, hasColor ? field : undefined);

    if (this.frameTriBuf) this.extrudeAllFrames(field, hasColor);
    if (this.shellExtrudeTriBuf) this.extrudeAllShells(field, hasColor);
  }

  private colorOf(field: DisplacementField, id: number): Vec3 {
    return [field.r?.[id] ?? 0.53, field.g?.[id] ?? 0.53, field.b?.[id] ?? 1];
  }

  /** Ported from FEMeshExporter.cs's ExtrudeFrame: box (4-gon) or cylinder
   *  (12-gon) prism between a frame's two deformed endpoints, oriented via an
   *  orthonormal basis built from the frame's own direction + a Z-up reference. */
  private extrudeAllFrames(field: DisplacementField, hasColor: boolean): void {
    const m = this.mesh!;
    const pos = this.frameTriBuf!;
    const col = hasColor ? this.frameTriColorBuf! : null;
    let po = 0, co = 0;

    const writeTri = (p: Vec3, q: Vec3, r: Vec3, cp: Vec3, cq: Vec3, cr: Vec3): void => {
      writeVertexMm(pos, po, p); po += 3;
      writeVertexMm(pos, po, q); po += 3;
      writeVertexMm(pos, po, r); po += 3;
      if (col) {
        col[co++] = cp[0]; col[co++] = cp[1]; col[co++] = cp[2];
        col[co++] = cq[0]; col[co++] = cq[1]; col[co++] = cq[2];
        col[co++] = cr[0]; col[co++] = cr[1]; col[co++] = cr[2];
      }
    };

    for (const fx of m.frameExtrude) {
      const a: Vec3 = [this.px[fx.p1], this.py[fx.p1], this.pz[fx.p1]];
      const b: Vec3 = [this.px[fx.p2], this.py[fx.p2], this.pz[fx.p2]];
      let dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
      const len = Math.hypot(dx, dy, dz);
      // A near-zero-length frame (both deformed endpoints coincide) has no defined
      // direction — fall back to an arbitrary axis rather than skipping outright:
      // skipping would leave this frame's slot in the fixed-size `pos`/`col` buffers
      // unwritten, shifting every following frame's triangles earlier and misaligning
      // the whole rest of the buffer for the remainder of the Play session.
      if (len < 1e-6) { dx = 0; dy = 0; dz = 1; } else { dx /= len; dy /= len; dz /= len; }

      let ux = 0, uy = 0, uz = 1;
      if (Math.abs(dz) > 0.98) { ux = 1; uy = 0; uz = 0; }

      let rx = dy * uz - dz * uy, ry = dz * ux - dx * uz, rz = dx * uy - dy * ux;
      const rlen = Math.hypot(rx, ry, rz);
      rx /= rlen; ry /= rlen; rz /= rlen;
      const ux2 = ry * dz - rz * dy, uy2 = rz * dx - rx * dz, uz2 = rx * dy - ry * dx;

      const sides = fx.poly.length;
      const c1: Vec3[] = new Array(sides);
      const c2: Vec3[] = new Array(sides);
      for (let i = 0; i < sides; i++) {
        const [lx, ly] = fx.poly[i];
        const ox = rx * lx + ux2 * ly, oy = ry * lx + uy2 * ly, oz = rz * lx + uz2 * ly;
        c1[i] = [a[0] + ox, a[1] + oy, a[2] + oz];
        c2[i] = [b[0] + ox, b[1] + oy, b[2] + oz];
      }

      const col1 = hasColor ? this.colorOf(field, fx.p1) : ([0.7, 0.7, 0.7] as Vec3);
      const col2 = hasColor ? this.colorOf(field, fx.p2) : ([0.7, 0.7, 0.7] as Vec3);

      for (let i = 0; i < sides; i++) {
        const j = (i + 1) % sides;
        writeTri(c1[i], c1[j], c2[j], col1, col1, col2);
        writeTri(c1[i], c2[j], c2[i], col1, col2, col2);
      }
      for (let i = 1; i < sides - 1; i++) {
        writeTri(c1[0], c1[i + 1], c1[i], col1, col1, col1);
        writeTri(c2[0], c2[i], c2[i + 1], col2, col2, col2);
      }
    }

    this.fe.applyFrameExtrude(pos, col);
  }

  /** Ported from FEMeshExporter.cs's ExtrudeShell: offset the shell's deformed
   *  corner ring by ±half its thickness along the ring's own normal, producing
   *  top/bottom caps + side walls. */
  private extrudeAllShells(field: DisplacementField, hasColor: boolean): void {
    const m = this.mesh!;
    const pos = this.shellExtrudeTriBuf!;
    const col = hasColor ? this.shellExtrudeTriColorBuf! : null;
    let po = 0, co = 0;

    const writeTri = (p: Vec3, q: Vec3, r: Vec3, cp: Vec3, cq: Vec3, cr: Vec3): void => {
      writeVertexMm(pos, po, p); po += 3;
      writeVertexMm(pos, po, q); po += 3;
      writeVertexMm(pos, po, r); po += 3;
      if (col) {
        col[co++] = cp[0]; col[co++] = cp[1]; col[co++] = cp[2];
        col[co++] = cq[0]; col[co++] = cq[1]; col[co++] = cq[2];
        col[co++] = cr[0]; col[co++] = cr[1]; col[co++] = cr[2];
      }
    };

    for (const sx of m.shellExtrude) {
      const n = sx.nodes.length;
      if (n < 3) continue;
      const pts: Vec3[] = sx.nodes.map((id): Vec3 => [this.px[id], this.py[id], this.pz[id]]);

      const e1x = pts[1][0] - pts[0][0], e1y = pts[1][1] - pts[0][1], e1z = pts[1][2] - pts[0][2];
      const e2x = pts[2][0] - pts[0][0], e2y = pts[2][1] - pts[0][1], e2z = pts[2][2] - pts[0][2];
      let nx = e1y * e2z - e1z * e2y, ny = e1z * e2x - e1x * e2z, nz = e1x * e2y - e1y * e2x;
      const nlen = Math.hypot(nx, ny, nz);
      // A degenerate normal (corners collinear in this deformed frame) has no defined
      // offset direction — fall back to an arbitrary axis rather than skipping outright,
      // for the same reason as the near-zero-length frame case above: skipping would
      // misalign every later shell's slot in the fixed-size `pos`/`col` buffers.
      if (nlen < 1e-9) { nx = 0; ny = 0; nz = 1; } else { nx /= nlen; ny /= nlen; nz /= nlen; }

      const half = sx.thickness / 2;
      const top: Vec3[] = new Array(n);
      const bot: Vec3[] = new Array(n);
      const cols: Vec3[] = new Array(n);
      for (let i = 0; i < n; i++) {
        top[i] = [pts[i][0] + nx * half, pts[i][1] + ny * half, pts[i][2] + nz * half];
        bot[i] = [pts[i][0] - nx * half, pts[i][1] - ny * half, pts[i][2] - nz * half];
        cols[i] = hasColor ? this.colorOf(field, sx.nodes[i]) : [0.7, 0.7, 0.7];
      }

      for (let i = 1; i < n - 1; i++) {
        writeTri(top[0], top[i], top[i + 1], cols[0], cols[i], cols[i + 1]);
        writeTri(bot[0], bot[i + 1], bot[i], cols[0], cols[i + 1], cols[i]);
      }
      for (let i = 0; i < n; i++) {
        const j = (i + 1) % n;
        writeTri(top[i], top[j], bot[j], cols[i], cols[j], cols[j]);
        writeTri(top[i], bot[j], bot[i], cols[i], cols[j], cols[i]);
      }
    }

    this.fe.applyShellExtrude(pos, col);
  }
}
